import React, { useState, useEffect, useMemo } from 'react';
import { Popconfirm, Tooltip, message, Popover } from 'antd';
import { DeleteOutlined, LoadingOutlined, EditOutlined, SmileOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Avatar from './Avatar';
import { SOURCE_BADGE } from '../constants';
import { getFullName } from '../utils/personUtils';
import { getDownloadUrl, getAttachmentPreviewUrl, updateComment } from '../../api/projectManagementApi';
import { formatBytes, getOptimisticImageUrl, setOptimisticImageUrl, isQuillEmpty } from '../../utils/attachmentUtils';
import { useTaskDrawer } from '../TaskDrawerContext';

/** Pick icon name + styling classes for file type */
function getFileIconInfo(attachment) {
    const name = (attachment?.originalName || attachment?.fileName || '').toLowerCase();
    const mime = (attachment?.mimeType || '').toLowerCase();

    if (mime.includes('pdf') || name.endsWith('.pdf'))
        return { icon: 'picture_as_pdf', bgClass: 'bg-red-50 dark:bg-red-950/80 text-red-600 dark:text-red-400 border border-red-200/80 dark:border-red-900/60', label: 'PDF' };
    if (mime.startsWith('image/') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.svg') || name.endsWith('.webp'))
        return { icon: 'image', bgClass: 'bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400 border border-blue-200/80 dark:border-blue-900/60', label: 'IMG' };
    if (mime.includes('word') || name.endsWith('.doc') || name.endsWith('.docx') || name.endsWith('.odt'))
        return { icon: 'description', bgClass: 'bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 border border-indigo-200/80 dark:border-indigo-900/60', label: 'DOC' };
    if (mime.includes('sheet') || name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv'))
        return { icon: 'table_chart', bgClass: 'bg-emerald-50 dark:bg-emerald-950/80 text-emerald-600 dark:text-emerald-400 border border-emerald-200/80 dark:border-emerald-900/60', label: 'XLS' };
    if (mime.includes('powerpoint') || name.endsWith('.ppt') || name.endsWith('.pptx'))
        return { icon: 'slideshow', bgClass: 'bg-orange-50 dark:bg-orange-950/80 text-orange-600 dark:text-orange-400 border border-orange-200/80 dark:border-orange-900/60', label: 'PPT' };
    if (mime.includes('zip') || mime.includes('rar') || name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z') || name.endsWith('.tar') || name.endsWith('.gz'))
        return { icon: 'folder_zip', bgClass: 'bg-amber-50 dark:bg-amber-950/80 text-amber-600 dark:text-amber-400 border border-amber-200/80 dark:border-amber-900/60', label: 'ZIP' };
    if (mime.includes('video/') || name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.mkv') || name.endsWith('.webm') || name.endsWith('.3gp'))
        return { icon: 'videocam', bgClass: 'bg-purple-50 dark:bg-purple-950/80 text-purple-600 dark:text-purple-400 border border-purple-200/80 dark:border-purple-900/60', label: 'VID' };
    if (mime.includes('audio/') || name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a') || name.endsWith('.ogg') || name.endsWith('.flac') || name.endsWith('.aac'))
        return { icon: 'audio_file', bgClass: 'bg-pink-50 dark:bg-pink-950/80 text-pink-600 dark:text-pink-400 border border-pink-200/80 dark:border-pink-900/60', label: 'AUD' };
    if (name.endsWith('.txt') || name.endsWith('.md'))
        return { icon: 'article', bgClass: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700', label: 'TXT' };
    return { icon: 'attach_file', bgClass: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700', label: 'FILE' };
}

/** Force downloads a file directly to the client's disk using Blob to bypass cross-origin browser restrictions */
async function triggerBlobDownload(url, filename) {
    if (!url) throw new Error('No URL provided');
    const res = await fetch(url);
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
}

/** Parses "📎 Attached: filename.jpg" style text comments into structured data */
function parseAttachmentText(text) {
    if (!text) return { cleanText: '', attachmentNames: [] };
    const attachmentNames = [];
    const cleanText = text
        .replace(/(.*?)\s*📎\s*Attached:\s*([^\n<]+)/g, (match, prefix, attachmentsStr) => {
            attachmentsStr.split(',').forEach(n => attachmentNames.push(n.trim()));
            return prefix;
        })
        .replace(/^(?:<p>\s*)?Added attachments(?:\s*<\/p>)?$/i, '')
        .trim();
    return { cleanText, attachmentNames };
}

/** Formats rich HTML comments to render inline @File[...] and @User badges */
function formatRichCommentHtml(html, employees = []) {
    if (!html) return '';
    let processed = html;

    // 1. Replace @File[filename] with interactive file badge HTML
    processed = processed.replace(/@File\[([^\]]+)\]/g, (match, fileName) => {
        const clean = (fileName || '').trim();
        return `<span class="inline-comment-file-chip inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded border border-blue-200 dark:border-blue-900/60 bg-blue-50/90 dark:bg-[#1a2538] text-blue-800 dark:text-blue-300 text-xs font-semibold cursor-pointer align-baseline hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors" data-filename="${clean}" title="Click to view file: ${clean}"><span class="material-symbols-outlined text-xs text-blue-600 dark:text-blue-400">attach_file</span><span class="truncate max-w-[150px] font-bold">${clean}</span><span class="text-[9px] text-blue-600 dark:text-blue-400 font-bold px-1 bg-blue-100 dark:bg-blue-900/40 rounded">@File</span></span>`;
    });

    // 2. Replace @User mentions with user mention badge HTML
    const candidates = [];
    (employees || []).forEach((emp) => {
        const full = getFullName(emp);
        if (full) candidates.push({ text: full, person: emp });
        if (emp.name && emp.name !== full) candidates.push({ text: emp.name, person: emp });
        const emailHandle = emp.email?.split('@')[0];
        if (emailHandle) candidates.push({ text: emailHandle, person: emp });
    });
    candidates.sort((a, b) => b.text.length - a.text.length);

    if (candidates.length > 0) {
        const escaped = candidates.map((c) => c.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const userRegex = new RegExp(`@(${escaped.join('|')})`, 'gi');
        processed = processed.replace(userRegex, (match, name) => {
            return `<span class="inline-comment-user-chip inline-flex items-center gap-0.5 px-1.5 py-0.5 mx-0.5 bg-[#eff4ff] dark:bg-[#1a2538] text-[#03488a] dark:text-[#a7c8ff] border border-[#03488a]/20 dark:border-blue-800/60 rounded font-semibold text-xs leading-none select-none align-baseline"><span class="text-[10px] font-bold text-[#00AAF1]">@</span><span>${name}</span></span>`;
        });
    }

    // 3. Auto-linkify raw URLs (http://, https://, www.) that are not already enclosed in <a> tags
    processed = processed.replace(
        /(<a\b[^>]*>[\s\S]*?<\/a>)|(<[^>]+>)|((?:https?:\/\/|www\.)[^\s<>"']+)/gi,
        (match, existingLink, htmlTag, rawUrl) => {
            if (existingLink || htmlTag) return match;
            let url = rawUrl;
            let trailing = '';
            const trailMatch = url.match(/[.,;:!)]+$/);
            if (trailMatch) {
                trailing = trailMatch[0];
                url = url.slice(0, -trailing.length);
            }
            const href = url.startsWith('http') ? url : `https://${url}`;
            return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="comment-inline-link text-[#0070F3] dark:text-[#38bdf8] underline hover:opacity-80 break-all font-medium cursor-pointer">${url}</a>${trailing}`;
        }
    );

    // 4. Strip broken, empty, blob:, or invalid img tags left from previous Quill client-side inserts
    processed = processed.replace(/<img\b(?![^>]*\bsrc=["']https?:\/\/)[^>]*>/gi, '');
    // Also clean up any resulting empty paragraphs <p></p> or <p><br></p>
    processed = processed.replace(/<p>\s*(?:<br\s*\/?>)?\s*<\/p>/gi, '');

    return processed;
}

function CommentAttachment({ attachment, taskId }) {
    const [downloading, setDownloading] = useState(false);
    const [viewing, setViewing] = useState(false);
    const { icon, bgClass } = getFileIconInfo(attachment);

    const isImg = (attachment?.mimeType || '').startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(attachment?.originalName || attachment?.fileName || '');

    // Synchronous optimistic lookup from R2 public URL, downloadUrl or session cache
    const initialUrl = attachment?.publicUrl
        || attachment?.previewUrl
        || (isImg && attachment?._id && taskId ? getAttachmentPreviewUrl(taskId, attachment._id) : null)
        || attachment?.downloadUrl
        || (attachment?._id ? getOptimisticImageUrl(attachment._id) : null)
        || (attachment?.originalName ? getOptimisticImageUrl(attachment.originalName) : null)
        || null;

    const [inlineUrl, setInlineUrl] = useState(initialUrl);

    useEffect(() => {
        if (isImg && !inlineUrl) {
            if (attachment?.downloadUrl) {
                setInlineUrl(attachment.downloadUrl);
                return;
            }
            const cached = (attachment?._id ? getOptimisticImageUrl(attachment._id) : null)
                || (attachment?.originalName ? getOptimisticImageUrl(attachment.originalName) : null);
            if (cached) {
                setInlineUrl(cached);
                return;
            }
            if (attachment?._id && taskId) {
                getDownloadUrl(taskId, attachment._id, 'inline')
                    .then(({ data }) => {
                        const url = data?.result?.downloadUrl || data?.result?.publicUrl;
                        if (url) {
                            setInlineUrl(url);
                            setOptimisticImageUrl(attachment._id, url);
                            if (attachment.originalName) setOptimisticImageUrl(attachment.originalName, url);
                        }
                    })
                    .catch(() => { });
            }
        }
    }, [isImg, inlineUrl, attachment?.downloadUrl, attachment?._id, attachment?.originalName, taskId]);

    const getUrl = async (disposition = 'inline') => {
        if (disposition === 'inline' && (inlineUrl || attachment?.publicUrl)) {
            return inlineUrl || attachment?.publicUrl;
        }
        const { data } = await getDownloadUrl(taskId, attachment._id, disposition);
        return data.result.downloadUrl;
    };

    const handleView = async () => {
        setViewing(true);
        try {
            const url = await getUrl('inline');
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            message.error('Could not open attachment.');
        } finally {
            setViewing(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const url = await getUrl('attachment');
            await triggerBlobDownload(url, attachment.originalName || attachment.fileName || 'attachment');
        } catch {
            const fallbackUrl = inlineUrl || attachment?.downloadUrl || attachment?.publicUrl;
            if (fallbackUrl) {
                window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
            } else {
                message.error('Could not download attachment.');
            }
        } finally {
            setDownloading(false);
        }
    };

    if (isImg) {
        return (
            <div
                onClick={handleView}
                className="group/img relative rounded-xl overflow-hidden border border-slate-200/90 dark:border-slate-700/80 bg-white dark:bg-[#1a2538] hover:shadow-md hover:border-[#03488a]/50 dark:hover:border-blue-500/50 transition-all cursor-pointer max-w-[280px]"
                title={`View image: ${attachment.originalName || 'image'}`}
            >
                <div className="relative w-full h-36 bg-slate-100 dark:bg-slate-900 overflow-hidden flex items-center justify-center">
                    {inlineUrl || attachment.publicUrl || attachment.downloadUrl ? (
                        <img
                            src={inlineUrl || attachment.publicUrl || attachment.downloadUrl}
                            alt={attachment.originalName || 'Attachment image'}
                            className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-300"
                            loading="lazy"
                            onError={() => setInlineUrl(null)}
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-1 text-slate-400">
                            {viewing ? (
                                <LoadingOutlined style={{ fontSize: 20 }} />
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-2xl text-blue-500">image</span>
                                    <span className="text-[11px]">Loading image…</span>
                                </>
                            )}
                        </div>
                    )}
                    {/* Hover overlay with action buttons */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Tooltip title="View full size">
                            <span className="p-1.5 bg-white/90 dark:bg-slate-800/90 rounded-lg text-slate-700 dark:text-slate-200 shadow-sm hover:scale-110 transition-transform flex items-center justify-center">
                                <span className="material-symbols-outlined text-base">zoom_in</span>
                            </span>
                        </Tooltip>
                        <Tooltip title="Download">
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                                disabled={downloading}
                                className="p-1.5 bg-white/90 dark:bg-slate-800/90 rounded-lg text-slate-700 dark:text-slate-200 shadow-sm hover:scale-110 transition-transform flex items-center justify-center border-none cursor-pointer"
                            >
                                {downloading ? (
                                    <LoadingOutlined style={{ fontSize: 14 }} />
                                ) : (
                                    <span className="material-symbols-outlined text-base">download</span>
                                )}
                            </button>
                        </Tooltip>
                    </div>
                </div>
                <div className="p-2 flex items-center justify-between gap-2 border-t border-slate-100 dark:border-slate-800">
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate" title={attachment.originalName}>
                            {attachment.originalName}
                        </p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">
                            {formatBytes(attachment.size)}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={handleView}
            className="flex items-center gap-2.5 p-2 bg-white dark:bg-[#1a2538] border border-slate-200/80 dark:border-slate-700/80 rounded-xl w-fit cursor-pointer hover:shadow-sm hover:border-[#03488a]/40 dark:hover:border-blue-500/50 transition-all group max-w-[260px]"
        >
            {/* File-type icon badge */}
            <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${bgClass}`}
            >
                <span className="material-symbols-outlined text-[19px]">
                    {icon}
                </span>
            </div>

            {/* Name + size */}
            <div className="flex-1 min-w-0">
                <div
                    className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate"
                    title={attachment.originalName}
                >
                    {attachment.originalName}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {formatBytes(attachment.size)}
                </div>
            </div>

            {/* Download button */}
            <Tooltip title="Download">
                <button
                    onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                    disabled={downloading || viewing}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-[#03488a] dark:hover:text-blue-400 p-1 flex-shrink-0 cursor-pointer"
                >
                    {downloading
                        ? <LoadingOutlined style={{ fontSize: 13 }} />
                        : <span className="material-symbols-outlined text-sm">download</span>
                    }
                </button>
            </Tooltip>
        </div>
    );
}

/** Interactive chip for plain-text "📎 Attached: filename" style comments */
function PlainAttachmentChip({ name, taskId, taskAttachments = [], commentFileReferences = [] }) {
    const [actionLoading, setActionLoading] = useState(false);
    const cleanName = (name || '').trim();

    // 1. Try to find matching attachment in task.attachments (newest first)
    const matchedAtt = [...(taskAttachments || [])].reverse().find((a) => {
        const attName = (a.originalName || a.fileName || '').trim().toLowerCase();
        const searchName = cleanName.toLowerCase();
        return attName === searchName || (attName && searchName.includes(attName)) || (searchName && attName.includes(searchName));
    });

    // 2. Try to find in comment's fileReferences (newest first)
    const matchedRef = [...(commentFileReferences || [])].reverse().find((r) => {
        const refName = (r.originalName || r.fileName || '').trim().toLowerCase();
        const searchName = cleanName.toLowerCase();
        return refName === searchName || (refName && searchName.includes(refName));
    });

    const fileMeta = matchedAtt || matchedRef || { originalName: cleanName, mimeType: '' };
    const { icon, bgClass } = getFileIconInfo(fileMeta);

    const isImg = (fileMeta?.mimeType || '').startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(cleanName);

    const initialUrl = matchedAtt?.publicUrl
        || matchedRef?.publicUrl
        || (matchedAtt?._id ? getOptimisticImageUrl(matchedAtt._id) : null)
        || getOptimisticImageUrl(cleanName)
        || null;

    const [inlineUrl, setInlineUrl] = useState(initialUrl);

    useEffect(() => {
        if (isImg && !inlineUrl) {
            const cached = (matchedAtt?._id ? getOptimisticImageUrl(matchedAtt._id) : null)
                || getOptimisticImageUrl(cleanName);
            if (cached) {
                setInlineUrl(cached);
                return;
            }
            if (matchedAtt?._id && taskId) {
                getDownloadUrl(taskId, matchedAtt._id, 'inline')
                    .then(({ data }) => {
                        const url = data?.result?.downloadUrl || data?.result?.publicUrl;
                        if (url) {
                            setInlineUrl(url);
                            setOptimisticImageUrl(matchedAtt._id, url);
                        }
                    })
                    .catch(() => { });
            }
        }
    }, [isImg, inlineUrl, cleanName, matchedAtt?._id, taskId]);

    const handleView = async (e) => {
        e.stopPropagation();
        if (inlineUrl) {
            window.open(inlineUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedAtt?.publicUrl) {
            window.open(matchedAtt.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedRef?.publicUrl) {
            window.open(matchedRef.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedAtt?._id && taskId) {
            setActionLoading(true);
            try {
                const { data } = await getDownloadUrl(taskId, matchedAtt._id, 'inline');
                if (data?.result?.downloadUrl) {
                    window.open(data.result.downloadUrl, '_blank', 'noopener,noreferrer');
                } else {
                    message.info(`File: ${cleanName}`);
                }
            } catch {
                message.error('Could not open attached file.');
            } finally {
                setActionLoading(false);
            }
            return;
        }
        message.info(`Attached file: ${cleanName}`);
    };

    const handleDownload = async (e) => {
        e.stopPropagation();
        if (matchedAtt?._id && taskId) {
            setActionLoading(true);
            try {
                const { data } = await getDownloadUrl(taskId, matchedAtt._id, 'attachment');
                const url = data?.result?.downloadUrl || matchedAtt.publicUrl || inlineUrl;
                if (url) {
                    await triggerBlobDownload(url, cleanName);
                    return;
                }
            } catch {
                message.error('Could not download file.');
            } finally {
                setActionLoading(false);
            }
            return;
        }
        const fallbackUrl = inlineUrl || matchedAtt?.publicUrl || matchedRef?.publicUrl;
        if (fallbackUrl) {
            try {
                await triggerBlobDownload(fallbackUrl, cleanName);
            } catch {
                window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
            }
            return;
        }
        message.info(`Attached file: ${cleanName}`);
    };

    if (isImg) {
        return (
            <div
                onClick={handleView}
                className="group/img relative rounded-xl overflow-hidden border border-slate-200/90 dark:border-slate-700/80 bg-white dark:bg-[#1a2538] hover:shadow-md hover:border-[#03488a]/50 dark:hover:border-blue-500/50 transition-all cursor-pointer max-w-[280px]"
                title={`View image: ${cleanName}`}
            >
                <div className="relative w-full h-36 bg-slate-100 dark:bg-slate-900 overflow-hidden flex items-center justify-center">
                    {inlineUrl || matchedAtt?.publicUrl || matchedRef?.publicUrl ? (
                        <img
                            src={inlineUrl || matchedAtt?.publicUrl || matchedRef?.publicUrl}
                            alt={cleanName}
                            className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-300"
                            loading="lazy"
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center gap-1 text-slate-400">
                            {actionLoading ? (
                                <LoadingOutlined style={{ fontSize: 20 }} />
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-2xl text-blue-500">image</span>
                                    <span className="text-[11px]">Loading image…</span>
                                </>
                            )}
                        </div>
                    )}
                    {/* Hover overlay with action buttons */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Tooltip title="View full size">
                            <span className="p-1.5 bg-white/90 dark:bg-slate-800/90 rounded-lg text-slate-700 dark:text-slate-200 shadow-sm hover:scale-110 transition-transform flex items-center justify-center">
                                <span className="material-symbols-outlined text-base">zoom_in</span>
                            </span>
                        </Tooltip>
                        <Tooltip title="Download">
                            <button
                                type="button"
                                onClick={handleDownload}
                                disabled={actionLoading}
                                className="p-1.5 bg-white/90 dark:bg-slate-800/90 rounded-lg text-slate-700 dark:text-slate-200 shadow-sm hover:scale-110 transition-transform flex items-center justify-center border-none cursor-pointer"
                            >
                                {actionLoading ? (
                                    <LoadingOutlined style={{ fontSize: 14 }} />
                                ) : (
                                    <span className="material-symbols-outlined text-base">download</span>
                                )}
                            </button>
                        </Tooltip>
                    </div>
                </div>
                <div className="p-2 flex items-center justify-between gap-2 border-t border-slate-100 dark:border-slate-800">
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate" title={cleanName}>
                            {cleanName}
                        </p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">
                            {matchedAtt?.size ? formatBytes(matchedAtt.size) : 'Image'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={handleView}
            className="flex items-center gap-2.5 p-2 bg-white dark:bg-[#1a2538] border border-slate-200/80 dark:border-slate-700/80 rounded-xl w-fit cursor-pointer hover:shadow-sm hover:border-[#03488a]/40 dark:hover:border-blue-500/50 transition-all group max-w-[260px]"
            title={`Attached File: ${cleanName} (Click to view / download)`}
        >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${bgClass}`}>
                <span className="material-symbols-outlined text-[18px]">{icon}</span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate" title={cleanName}>{cleanName}</div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Attached file</div>
            </div>
            {actionLoading ? (
                <LoadingOutlined style={{ fontSize: 12 }} />
            ) : (
                <Tooltip title="Download">
                    <button
                        type="button"
                        onClick={handleDownload}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-[#03488a] dark:hover:text-blue-400 p-1 flex-shrink-0 cursor-pointer"
                    >
                        <span className="material-symbols-outlined text-sm">download</span>
                    </button>
                </Tooltip>
            )}
        </div>
    );
}

/** Chip for linked @File project storage references */
function CommentFileReferenceChip({ fileRef, taskId, taskAttachments = [] }) {
    const [actionLoading, setActionLoading] = useState(false);
    const name = fileRef.originalName || fileRef.fileName || 'Referenced File';
    const folderLabel = fileRef.revisionRound || (fileRef.folderCode ? `Folder ${fileRef.folderCode}` : 'Project File');

    // Find if this file also exists as an attachment on the task
    const matchedAtt = taskAttachments.find(
        (a) => (a.originalName || a.fileName || '').trim().toLowerCase() === name.trim().toLowerCase()
    );

    const handleClick = async () => {
        if (fileRef.publicUrl) {
            window.open(fileRef.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedAtt?.publicUrl) {
            window.open(matchedAtt.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedAtt?._id && taskId) {
            setActionLoading(true);
            try {
                const { data } = await getDownloadUrl(taskId, matchedAtt._id, 'inline');
                if (data?.result?.downloadUrl) {
                    window.open(data.result.downloadUrl, '_blank', 'noopener,noreferrer');
                } else {
                    message.info(`File reference: ${name}`);
                }
            } catch {
                message.error('Could not open referenced file.');
            } finally {
                setActionLoading(false);
            }
            return;
        }
        message.info(`File reference: ${name}`);
    };

    return (
        <div
            onClick={handleClick}
            className="inline-flex items-center gap-2 p-2 bg-[#eff4ff] dark:bg-[#1a2538] border border-[#0070F3]/30 dark:border-[#0070F3]/50 rounded-xl text-xs cursor-pointer hover:shadow-sm hover:border-[#0070F3] transition-all group max-w-[280px]"
            title={`Referenced Project File: ${name}`}
        >
            <div className="w-8 h-8 rounded-lg bg-[#0070F3]/15 text-[#0070F3] dark:text-[#60a5fa] flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-[18px]">folder_shared</span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-[#0b1c30] dark:text-[#eaf1ff] truncate">
                    {name}
                </div>
                <div className="text-[10px] text-[#0070F3] font-medium truncate flex items-center gap-1">
                    <span>@File</span>
                    <span className="opacity-70">• {folderLabel}</span>
                </div>
            </div>
            {actionLoading ? (
                <LoadingOutlined style={{ fontSize: 13, color: '#0070F3' }} />
            ) : (
                <span className="material-symbols-outlined text-sm text-[#727786] group-hover:text-[#0070F3] transition-colors flex-shrink-0">
                    open_in_new
                </span>
            )}
        </div>
    );
}

/** Interactive inline chip for @File[filename] occurrences in comment body */
function InlineFileMentionChip({ fileName, taskAttachments = [], fileReferences = [], taskId }) {
    const [actionLoading, setActionLoading] = useState(false);
    const cleanName = (fileName || '').trim();

    const matchedAtt = [...(taskAttachments || [])].reverse().find(
        (a) => (a.originalName || a.fileName || '').trim().toLowerCase() === cleanName.toLowerCase()
    );
    const matchedRef = [...(fileReferences || [])].reverse().find(
        (r) => (r.originalName || r.fileName || '').trim().toLowerCase() === cleanName.toLowerCase()
    );

    const fileMeta = matchedAtt || matchedRef || { originalName: cleanName, mimeType: '' };
    const { icon, bgClass } = getFileIconInfo(fileMeta);

    const handleView = async (e) => {
        e.stopPropagation();
        if (matchedAtt?.publicUrl) {
            window.open(matchedAtt.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedRef?.publicUrl) {
            window.open(matchedRef.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matchedAtt?._id && taskId) {
            setActionLoading(true);
            try {
                const { data } = await getDownloadUrl(taskId, matchedAtt._id, 'inline');
                if (data?.result?.downloadUrl) {
                    window.open(data.result.downloadUrl, '_blank', 'noopener,noreferrer');
                } else {
                    message.info(`File: ${cleanName}`);
                }
            } catch {
                message.error('Could not open referenced file.');
            } finally {
                setActionLoading(false);
            }
            return;
        }
        message.info(`Referenced file: ${cleanName}`);
    };

    const handleDownload = async (e) => {
        e.stopPropagation();
        if (matchedAtt?._id && taskId) {
            setActionLoading(true);
            try {
                const { data } = await getDownloadUrl(taskId, matchedAtt._id, 'attachment');
                const url = data?.result?.downloadUrl || matchedAtt.publicUrl;
                if (url) {
                    await triggerBlobDownload(url, cleanName);
                    return;
                }
            } catch {
                message.error('Could not download file.');
            } finally {
                setActionLoading(false);
            }
            return;
        }
        if (matchedAtt?.publicUrl || matchedRef?.publicUrl) {
            const url = matchedAtt?.publicUrl || matchedRef?.publicUrl;
            try {
                await triggerBlobDownload(url, cleanName);
            } catch {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
            return;
        }
        message.info(`File reference: ${cleanName}`);
    };

    return (
        <span
            onClick={handleView}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 mx-1 my-0.5 rounded-lg border border-blue-200 dark:border-blue-900/60 bg-blue-50/90 dark:bg-[#1a2538] hover:bg-blue-100/90 dark:hover:bg-[#22324d] text-blue-800 dark:text-blue-300 text-xs font-medium cursor-pointer shadow-xs transition-all align-middle group/filechip select-none"
            title={`Referenced File: ${cleanName} (Click to view / download)`}
        >
            <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${bgClass}`}>
                <span className="material-symbols-outlined text-[14px]">
                    {icon}
                </span>
            </span>
            <span className="truncate max-w-[220px] font-semibold">{cleanName}</span>
            <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold px-1 py-0.2 bg-blue-100 dark:bg-blue-900/40 rounded">
                @File
            </span>
            <Tooltip title="Download file">
                <button
                    type="button"
                    onClick={handleDownload}
                    disabled={actionLoading}
                    className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-300 p-0.5 transition-colors flex items-center cursor-pointer"
                >
                    {actionLoading ? (
                        <LoadingOutlined style={{ fontSize: 11 }} />
                    ) : (
                        <span className="material-symbols-outlined text-[13px]">download</span>
                    )}
                </button>
            </Tooltip>
        </span>
    );
}

/** Renders a highlighted, interactive mention pill for users (@Name) */
function InlineUserMentionChip({ person, name }) {
    const displayName = person ? getFullName(person) : (name ? name.replace(/\b\w/g, (c) => c.toUpperCase()) : 'User');
    return (
        <span
            className="inline-flex items-center gap-0.5 px-2 py-0.5 mx-0.5 bg-[#eff4ff] dark:bg-[#1a2538] text-[#03488a] dark:text-[#a7c8ff] border border-[#03488a]/20 dark:border-blue-800/60 rounded-md font-semibold text-xs leading-none select-none align-baseline shadow-2xs hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
            title={person?.email ? `${displayName} (${person.email})` : displayName}
        >
            <span className="text-[11px] font-bold text-[#00AAF1]">@</span>
            <span>${displayName}</span>
        </span>
    );
}

function renderTextWithLinks(text) {
    if (!text || typeof text !== 'string') return text;
    const urlRegex = /((?:https?:\/\/|www\.)[^\s]+)/gi;
    if (!urlRegex.test(text)) return text;
    urlRegex.lastIndex = 0;
    const parts = [];
    let lastIdx = 0;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
        if (match.index > lastIdx) {
            parts.push(text.slice(lastIdx, match.index));
        }
        let url = match[1];
        let trailing = '';
        const trailMatch = url.match(/[.,;:!)]+$/);
        if (trailMatch) {
            trailing = trailMatch[0];
            url = url.slice(0, -trailing.length);
        }
        const href = url.startsWith('http') ? url : `https://${url}`;
        parts.push(
            <a
                key={`link-${match.index}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[#0070F3] dark:text-[#38bdf8] underline hover:opacity-80 break-all font-medium cursor-pointer"
            >
                {url}
            </a>
        );
        if (trailing) parts.push(trailing);
        lastIdx = urlRegex.lastIndex;
    }
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));
    return parts;
}

/** Parses plain text chunks for @User mentions and returns mixed text + chips */
function parseTextWithUserMentions(text, employees = []) {
    if (!text || typeof text !== 'string') return text;

    // Collect all candidate employee names & handles sorted by length descending
    const candidates = [];
    (employees || []).forEach((emp) => {
        const full = getFullName(emp);
        if (full) candidates.push({ text: full, person: emp });
        if (emp.name && emp.name !== full) candidates.push({ text: emp.name, person: emp });
        const emailHandle = emp.email?.split('@')[0];
        if (emailHandle) candidates.push({ text: emailHandle, person: emp });
    });

    candidates.sort((a, b) => b.text.length - a.text.length);

    // Build regex that looks for candidate names first, or any standard handle
    const escapedNames = candidates.map((c) => c.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const mentionPattern = escapedNames.length > 0
        ? new RegExp(`@(${escapedNames.join('|')}|[a-zA-Z0-9._-]+)`, 'gi')
        : /@([a-zA-Z0-9._-]+)/gi;

    if (!mentionPattern.test(text)) {
        return renderTextWithLinks(text);
    }
    mentionPattern.lastIndex = 0;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionPattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(renderTextWithLinks(text.slice(lastIndex, match.index)));
        }

        const matchedRaw = match[1];
        const lowerMatched = matchedRaw.toLowerCase();
        const matchedCandidate = candidates.find(
            (c) => c.text.toLowerCase() === lowerMatched
        );

        parts.push(
            <InlineUserMentionChip
                key={`user-mention-${match.index}`}
                person={matchedCandidate?.person || null}
                name={matchedCandidate ? matchedCandidate.text : matchedRaw}
            />
        );

        lastIndex = mentionPattern.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(renderTextWithLinks(text.slice(lastIndex)));
    }

    return parts;
}

/** Renders comment body parsing both @File[...] and @User mentions into interactive chips */
function renderCommentBody(text, taskAttachments, fileReferences, taskId, employees = []) {
    if (!text) return null;
    const fileMentionRegex = /@File\[([^\]]+)\]/g;

    // Case 1: No file mentions — just parse user mentions on the entire string
    if (!fileMentionRegex.test(text)) {
        return parseTextWithUserMentions(text, employees);
    }
    fileMentionRegex.lastIndex = 0;

    // Case 2: Mixed file mentions and user mentions
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = fileMentionRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            const textChunk = text.slice(lastIndex, match.index);
            parts.push(parseTextWithUserMentions(textChunk, employees));
        }
        const fileName = match[1];
        parts.push(
            <InlineFileMentionChip
                key={`file-mention-${match.index}`}
                fileName={fileName}
                taskAttachments={taskAttachments}
                fileReferences={fileReferences}
                taskId={taskId}
            />
        );
        lastIndex = fileMentionRegex.lastIndex;
    }

    if (lastIndex < text.length) {
        const textChunk = text.slice(lastIndex);
        parts.push(parseTextWithUserMentions(textChunk, employees));
    }

    return parts;
}

const commentEditQuillModules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['link'],
        ['clean'],
    ],
};

const commentEditQuillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'blockquote', 'code-block',
    'link', 'image',
];

export default function CommentItem({ comment, currentAdminId, onDelete, taskId }) {
    const { task, setTask, employees = [], onTaskUpdated, handleToggleCommentReaction } = useTaskDrawer();
    const taskAttachments = task?.attachments || [];
    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(comment.text || '');
    const [savingEdit, setSavingEdit] = useState(false);

    // Check if user can edit: only author, within 1 hour of creation
    const canEdit = String(comment.author?._id || comment.author) === String(currentAdminId) &&
        dayjs().diff(dayjs(comment.createdAt), 'minute') <= 60;
    const isAuthor = String(comment.author?._id || comment.author) === String(currentAdminId);

    const authorName = getFullName(comment.author) || 'Unknown';
    const badge = SOURCE_BADGE[comment.source] || SOURCE_BADGE.manual;
    const isOwn = comment.author?._id === currentAdminId;
    const canDelete = isOwn && !comment.isSystemGenerated;

    // Structured attachments (API objects)
    const hasAttachments = (comment.attachments?.length || 0) > 0;

    // Parse plain-text fallback attachments (📎 Attached: filename)
    const { cleanText, attachmentNames } = parseAttachmentText(comment.text);
    const hasPlainAttachments = !hasAttachments && attachmentNames.length > 0;

    const handleSaveEdit = async () => {
        const textEmpty = !editedText || isQuillEmpty(editedText) || editedText.trim() === '' || editedText.trim() === '<p><br></p>';
        if (textEmpty) {
            message.warning('Comment text cannot be empty.');
            return;
        }
        setSavingEdit(true);
        try {
            const { data } = await updateComment(taskId, comment._id, editedText);
            if (data?.success && data?.result) {
                const updatedComment = data.result;
                if (setTask) {
                    setTask((prev) => {
                        if (!prev?.comments) return prev;
                        const nextComments = prev.comments.map((c) =>
                            String(c._id || c) === String(comment._id) ? updatedComment : c
                        );
                        return { ...prev, comments: nextComments };
                    });
                }
                onTaskUpdated?.({
                    ...(task || {}),
                    comments: (task?.comments || []).map((c) =>
                        String(c._id || c) === String(comment._id) ? updatedComment : c
                    ),
                });
                setIsEditing(false);
                message.success('Comment updated successfully.');
            }
        } catch (err) {
            console.error('Error updating comment:', err);
            message.error(err?.response?.data?.message || 'Failed to update comment.');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleToggleReaction = (emoji) => {
        if (!currentAdminId || !comment._id) return;
        handleToggleCommentReaction?.(comment._id, emoji);
    };

    const groupedReactions = useMemo(() => {
        const raw = Array.isArray(comment.reactions) ? comment.reactions : [];
        const groups = {};
        raw.forEach((r) => {
            if (!r?.emoji) return;
            if (!groups[r.emoji]) groups[r.emoji] = { emoji: r.emoji, count: 0, users: [], hasReacted: false };
            groups[r.emoji].count += 1;
            if (r.userName) groups[r.emoji].users.push(r.userName);
            const rUserId = String(r.user?._id || r.user || r.userId || '');
            if (rUserId === String(currentAdminId)) {
                groups[r.emoji].hasReacted = true;
            }
        });
        return Object.values(groups);
    }, [comment.reactions, currentAdminId]);

    const handleOpenFileByName = async (targetFileName) => {
        const cleanTarget = (targetFileName || '').trim();
        const matched = (taskAttachments || []).find((a) => {
            const aName = (a.originalName || a.fileName || '').trim().toLowerCase();
            const sName = cleanTarget.toLowerCase();
            return aName === sName || (aName && sName.includes(aName)) || (sName && aName.includes(sName));
        });

        if (matched?.publicUrl) {
            window.open(matched.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (matched?._id && taskId) {
            try {
                const { data } = await getDownloadUrl(taskId, matched._id, 'inline');
                const url = data?.result?.downloadUrl || data?.result?.publicUrl;
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                else message.info(`File: ${cleanTarget}`);
            } catch {
                message.error('Could not open referenced file.');
            }
            return;
        }
        message.info(`File reference: ${cleanTarget}`);
    };

    return (
        <div id={`comment-${comment._id}`} className="flex gap-3 group rounded-xl p-1 transition-all duration-300">
            <style>{`
                .comment-quill-content.ql-snow {
                    border: none !important;
                }
                .comment-quill-content .ql-editor {
                    padding: 0 !important;
                    font-size: 13px;
                    line-height: 1.5;
                    color: inherit;
                    min-height: auto;
                }
                .comment-quill-content .ql-editor p {
                    margin-bottom: 0.35rem;
                }
                .comment-quill-content .ql-editor p:last-child {
                    margin-bottom: 0;
                }
                .comment-quill-content .ql-editor ul,
                .comment-quill-content .ql-editor ol {
                    padding-left: 1.25rem;
                    margin-bottom: 0.35rem;
                }
                .comment-quill-content .ql-editor pre.ql-syntax {
                    background-color: #1e293b;
                    color: #f8fafc;
                    border-radius: 0.5rem;
                    padding: 0.5rem 0.75rem;
                    font-size: 12px;
                    margin: 0.35rem 0;
                }
                .comment-quill-content .ql-editor blockquote {
                    border-left: 3px solid #03488a;
                    padding-left: 0.75rem;
                    color: #64748b;
                    margin: 0.35rem 0;
                }
                .dark .comment-quill-content .ql-editor blockquote {
                    border-left-color: #38bdf8;
                    color: #94a3b8;
                }
                .comment-quill-content .ql-editor img {
                    max-width: 100%;
                    border-radius: 0.5rem;
                    margin: 0.35rem 0;
                    cursor: pointer;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    transition: transform 0.15s ease;
                }
                .comment-quill-content .ql-editor img:hover {
                    transform: scale(1.01);
                }
                .comment-quill-content .ql-editor a {
                    color: #0070F3 !important;
                    text-decoration: underline !important;
                    word-break: break-all;
                    cursor: pointer;
                }
                .comment-quill-content .ql-editor a:hover {
                    color: #0051b3 !important;
                    opacity: 0.85;
                }
                .dark .comment-quill-content .ql-editor a {
                    color: #38bdf8 !important;
                }
                .dark .comment-quill-content .ql-editor a:hover {
                    color: #7dd3fc !important;
                }
                .comment-quill-wrapper .ql-snow {
                    border: none !important;
                }
                .comment-quill-wrapper .ql-toolbar.ql-snow {
                    border-bottom: 1px solid #e2e8f0 !important;
                    background: #f8fafc;
                    padding: 4px 8px !important;
                }
                .comment-quill-wrapper .ql-editor {
                    min-height: 60px;
                    max-height: 220px;
                    font-size: 13px;
                    padding: 8px 12px !important;
                }
                .dark .comment-quill-wrapper .ql-toolbar.ql-snow {
                    background: #182234 !important;
                    border-bottom-color: #334155 !important;
                }
                .dark .comment-quill-wrapper .ql-toolbar.ql-snow .ql-stroke {
                    stroke: #94a3b8 !important;
                }
                .dark .comment-quill-wrapper .ql-toolbar.ql-snow .ql-fill {
                    fill: #94a3b8 !important;
                }
                .dark .comment-quill-wrapper .ql-toolbar.ql-snow .ql-picker {
                    color: #94a3b8 !important;
                }
                .dark .comment-quill-wrapper .ql-container.ql-snow {
                    background: #141414 !important;
                }
                .dark .comment-quill-wrapper .ql-editor {
                    color: #f1f5f9;
                }
            `}</style>
            <Avatar person={comment.author} name={authorName} size={32} />
            <div className="flex-1 min-w-0 space-y-1">
                {/* Name + timestamp row */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{authorName}</span>
                        {badge.label && (
                            <span className={`text-sm font-bold px-1.5 py-0.5 rounded ${badge.color}`}>
                                {badge.label}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Tooltip title={dayjs(comment.createdAt).format('dddd, MMMM D, YYYY · h:mm:ss A')}>
                            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 cursor-default">
                                {dayjs(comment.createdAt).format('MMM D, YYYY · h:mm A')}
                            </span>
                        </Tooltip>
                        {comment.isEdited && (
                            <Tooltip title={comment.editedAt ? `Edited on ${dayjs(comment.editedAt).format('MMM D, YYYY · h:mm:ss A')}` : 'Edited'}>
                                <span className="text-[11px] text-slate-400 dark:text-slate-500 italic cursor-default">
                                    (edited)
                                </span>
                            </Tooltip>
                        )}

                        {/* Reaction Picker Popover */}
                        <Popover
                            content={
                                <div className="flex items-center gap-1.5 p-1 text-base">
                                    {['👍', '✅', '❤️', '🎉', '🚀', '👀', '🔥', '👏', '💯', '💡'].map((emoji) => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => handleToggleReaction(emoji)}
                                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg hover:scale-125 transition-transform cursor-pointer"
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            }
                            trigger="click"
                            placement="topRight"
                        >
                            <button
                                type="button"
                                className="text-slate-400 hover:text-amber-500 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                                title="Add reaction"
                            >
                                <SmileOutlined style={{ fontSize: 13 }} />
                            </button>
                        </Popover>

                        {/* Edit button — visible to author within 1 hour */}
                        {canEdit && (
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(true);
                                    setEditedText(comment.text || '');
                                }}
                                className="text-slate-400 hover:text-[#03488a] dark:hover:text-[#a7c8ff] transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                                title="Edit comment (available for 1 hour)"
                            >
                                <EditOutlined style={{ fontSize: 12 }} />
                            </button>
                        )}

                        {canDelete && (
                            <Popconfirm
                                title="Delete this comment?"
                                onConfirm={() => onDelete(comment._id)}
                                okText="Delete"
                                okButtonProps={{ danger: true, size: 'small' }}
                                cancelText="Cancel"
                                placement="topRight"
                            >
                                <button className="text-slate-400 hover:text-error transition-colors opacity-0 group-hover:opacity-100 cursor-pointer">
                                    <DeleteOutlined style={{ fontSize: 12 }} />
                                </button>
                            </Popconfirm>
                        )}
                    </div>
                </div>

                {/* Edit Form or Comment Bubble */}
                {isEditing ? (
                    <div className="space-y-2 bg-slate-50 dark:bg-[#1a2538] border border-blue-200 dark:border-blue-900/60 p-2.5 rounded-xl rounded-tl-none">
                        <div className="comment-quill-wrapper rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#141414] overflow-hidden focus-within:ring-1 focus-within:ring-[#03488a]">
                            <ReactQuill
                                theme="snow"
                                value={editedText}
                                onChange={(content) => setEditedText(content)}
                                modules={commentEditQuillModules}
                                formats={commentEditQuillFormats}
                                placeholder="Edit your comment..."
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                disabled={savingEdit}
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditedText(comment.text || '');
                                }}
                                className="px-2.5 py-1 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md font-medium transition-colors cursor-pointer disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={savingEdit}
                                onClick={handleSaveEdit}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1 text-xs font-semibold text-white bg-[#03488a] hover:bg-[#003263] rounded-md shadow-xs transition-colors cursor-pointer disabled:opacity-50"
                            >
                                {savingEdit && <LoadingOutlined style={{ fontSize: 11 }} />}
                                <span>Save</span>
                            </button>
                        </div>
                    </div>
                ) : (cleanText || (!hasPlainAttachments && !hasAttachments)) && (() => {
                    const rawContent = cleanText || comment.text || '';
                    const isHtml = /<[a-z][\s\S]*>/i.test(rawContent);

                    if (isHtml) {
                        const formattedCommentHtml = formatRichCommentHtml(rawContent, employees);
                        return (
                            <div
                                onClick={(e) => {
                                    if (e.target.tagName === 'IMG') {
                                        e.stopPropagation();
                                        const src = e.target.getAttribute('src');
                                        if (src) window.open(src, '_blank', 'noopener,noreferrer');
                                    } else if (e.target.closest('.inline-comment-file-chip')) {
                                        e.stopPropagation();
                                        const chip = e.target.closest('.inline-comment-file-chip');
                                        const targetFile = chip?.getAttribute('data-filename');
                                        if (targetFile) handleOpenFileByName(targetFile);
                                    } else if (e.target.tagName === 'A' || e.target.closest('a')) {
                                        e.stopPropagation();
                                        const anchor = e.target.tagName === 'A' ? e.target : e.target.closest('a');
                                        const href = anchor?.getAttribute('href');
                                        if (href) window.open(href, '_blank', 'noopener,noreferrer');
                                    }
                                }}
                                className="task-quill-view comment-quill-content ql-snow text-sm text-slate-800 dark:text-slate-100 bg-[#f1f4f7] dark:bg-[#262626] border border-transparent dark:border-[#383838] p-3 rounded-xl rounded-tl-none leading-relaxed"
                            >
                                <div
                                    className="ql-editor p-0 max-w-none"
                                    dangerouslySetInnerHTML={{ __html: formattedCommentHtml }}
                                />
                            </div>
                        );
                    }

                    return (
                        <p className="text-sm text-slate-800 dark:text-slate-100 bg-[#f1f4f7] dark:bg-[#262626] border border-transparent dark:border-[#383838] p-3 rounded-xl rounded-tl-none leading-relaxed whitespace-pre-wrap">
                            {renderCommentBody(rawContent, taskAttachments, comment.fileReferences, taskId, employees)}
                        </p>
                    );
                })()}

                {/* Grouped Reactions Bar */}
                {groupedReactions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        {groupedReactions.map((r) => (
                            <Tooltip
                                key={r.emoji}
                                title={r.users.length > 0 ? r.users.join(', ') : `${r.count} reaction`}
                            >
                                <button
                                    type="button"
                                    onClick={() => handleToggleReaction(r.emoji)}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
                                        r.hasReacted
                                            ? 'bg-blue-50 dark:bg-blue-950/80 border-blue-300 dark:border-blue-800 text-[#03488a] dark:text-[#a7c8ff]'
                                            : 'bg-slate-100 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                                    }`}
                                >
                                    <span>{r.emoji}</span>
                                    <span className="text-[11px] font-bold">{r.count}</span>
                                </button>
                            </Tooltip>
                        ))}
                    </div>
                )}

                {/* Structured attachment cards (rich API objects) */}
                {hasAttachments && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {comment.attachments.map((att) => (
                            <CommentAttachment key={att._id} attachment={att} taskId={taskId} />
                        ))}
                    </div>
                )}

                {/* Fallback plain-text attachment chips */}
                {hasPlainAttachments && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {attachmentNames.map((name, i) => {
                            const matched = [...(taskAttachments || [])].reverse().find(att => (att.originalName || att.fileName || '').trim().toLowerCase() === name.trim().toLowerCase());
                            if (matched) {
                                return <CommentAttachment key={matched._id || i} attachment={matched} taskId={taskId} />;
                            }
                            return <PlainAttachmentChip key={i} name={name} taskId={taskId} taskAttachments={taskAttachments} commentFileReferences={comment.fileReferences} />;
                        })}
                    </div>
                )}

                {/* Structured @File project references */}
                {comment.fileReferences && comment.fileReferences.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {comment.fileReferences.map((ref, idx) => (
                            <CommentFileReferenceChip key={ref._id || idx} fileRef={ref} taskId={taskId} taskAttachments={taskAttachments} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
